import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { publicAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  GlobeAltIcon,
  PhoneIcon,
  EnvelopeIcon,
  TruckIcon,
  MapPinIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import TrackingResult from '../components/TrackingResult';

const TrackingPage = () => {
  const { trackingNumber: urlTrackingNumber } = useParams();
  const [trackingNumber, setTrackingNumber] = useState(urlTrackingNumber || '');
  const [searchedTrackingNumber, setSearchedTrackingNumber] = useState(urlTrackingNumber || '');
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Query for tracking data
  const { data: trackingData, isLoading, error } = useQuery({
    queryKey: ['tracking', searchedTrackingNumber],
    queryFn: () => publicAPI.trackPackage(searchedTrackingNumber),
    enabled: !!searchedTrackingNumber,
    retry: false,
  });

  useEffect(() => {
    if (urlTrackingNumber && urlTrackingNumber !== searchedTrackingNumber) {
      setSearchedTrackingNumber(urlTrackingNumber);
      setTrackingNumber(urlTrackingNumber);
    }
  }, [urlTrackingNumber, searchedTrackingNumber]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      const trimmedNumber = trackingNumber.trim().toUpperCase();
      setSearchedTrackingNumber(trimmedNumber);
      navigate(`/track/${trimmedNumber}`);
    } else {
      toast.error(t.tracking.pleaseEnter);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Compact search bar */}
      <section className="bg-[#0B1F3A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6 md:py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center space-x-2">
                <MagnifyingGlassIcon className="h-6 w-6 sm:h-7 sm:w-7 text-[#FF6B00]" />
                <span>{t.tracking.title}</span>
              </h1>
              <p className="text-gray-300 text-xs sm:text-sm mt-1">{t.tracking.subtitle}</p>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 w-full md:w-auto md:min-w-[480px]">
              <input
                id="tracking"
                type="text"
                placeholder={t.tracking.placeholder}
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-[#17202A] border-2 border-[#E2E8F0] rounded-lg focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-100 focus:outline-none text-sm md:text-base"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-5 sm:px-6 py-2.5 sm:py-3 bg-[#FF6B00] text-white hover:bg-[#e65f00] font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2 whitespace-nowrap text-sm sm:text-base"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <MagnifyingGlassIcon className="h-5 w-5" />
                )}
                <span>{t.tracking.trackNow}</span>
              </button>
            </form>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">

        {/* Error State */}
        {error && (
          <div className="card max-w-3xl mx-auto bg-red-50 border-red-200 mb-8">
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-[#DC2626]" />
              <div>
                <h3 className="text-lg font-medium text-[#DC2626]">
                  {t.tracking.notFound}
                </h3>
                <p className="text-red-700">
                  {error.response?.data?.message || t.tracking.notFoundDesc}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tracking Results */}
        {trackingData?.success && trackingData.data && (
          <TrackingResult trackingData={trackingData} />
        )}

        {/* Demo Tracking Numbers */}
        {!searchedTrackingNumber && !error && (
          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircleIcon className="h-5 w-5 text-[#FF6B00]" />
              <h3 className="text-base sm:text-lg font-medium text-[#0B1F3A]">
                {t.tracking.tryDemo}
              </h3>
            </div>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              {t.tracking.demoDesc}
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={() => setTrackingNumber('SC20231201123456')}
                className="text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 bg-orange-50 border-2 border-orange-200 text-[#FF6B00] rounded-lg hover:bg-orange-100 hover:border-orange-300 transition-all font-medium"
              >
                SC20231201123456
              </button>
              <button
                onClick={() => setTrackingNumber('SC20231202789012')}
                className="text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 bg-orange-50 border-2 border-orange-200 text-[#FF6B00] rounded-lg hover:bg-orange-100 hover:border-orange-300 transition-all font-medium"
              >
                SC20231202789012
              </button>
            </div>
          </div>
        )}

        {/* How It Works — only show when no results */}
        {!searchedTrackingNumber && !error && (
          <div className="mb-8 sm:mb-10">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-block bg-orange-100 text-[#FF6B00] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3">
                {t.tracking.simpleProcess}
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-2">{t.tracking.howTrackingWorks}</h2>
              <p className="text-sm sm:text-base text-[#64748B]">{t.tracking.howTrackingDesc}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-[#0B1F3A] rounded-full mb-4 shadow-md">
                  <MagnifyingGlassIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="font-bold text-sm sm:text-base text-[#0B1F3A] mb-2">{t.tracking.step1Title}</h3>
                <p className="text-xs sm:text-sm text-[#64748B]">{t.tracking.step1Desc}</p>
                <ArrowRightIcon className="hidden md:block absolute top-1/2 -right-4 h-6 w-6 text-gray-300" />
              </div>
              <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-[#0B1F3A] rounded-full mb-4 shadow-md">
                  <MapPinIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="font-bold text-sm sm:text-base text-[#0B1F3A] mb-2">{t.tracking.step2Title}</h3>
                <p className="text-xs sm:text-sm text-[#64748B]">{t.tracking.step2Desc}</p>
                <ArrowRightIcon className="hidden md:block absolute top-1/2 -right-4 h-6 w-6 text-gray-300" />
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-[#0B1F3A] rounded-full mb-4 shadow-md">
                  <TruckIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="font-bold text-sm sm:text-base text-[#0B1F3A] mb-2">{t.tracking.step3Title}</h3>
                <p className="text-xs sm:text-sm text-[#64748B]">{t.tracking.step3Desc}</p>
              </div>
            </div>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          <div className="text-center p-4 sm:p-5 md:p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full mb-3">
              <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF6B00]" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-[#0B1F3A] mb-1">{t.tracking.secure}</h3>
            <p className="text-xs sm:text-sm text-[#64748B]">{t.tracking.secureDesc}</p>
          </div>
          <div className="text-center p-4 sm:p-5 md:p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full mb-3">
              <GlobeAltIcon className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF6B00]" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-[#0B1F3A] mb-1">{t.tracking.globalCoverage}</h3>
            <p className="text-xs sm:text-sm text-[#64748B]">{t.tracking.globalCoverageDesc}</p>
          </div>
          <div className="text-center p-4 sm:p-5 md:p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full mb-3">
              <PhoneIcon className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF6B00]" />
            </div>
            <h3 className="font-semibold text-sm sm:text-base text-[#0B1F3A] mb-1">{t.tracking.support247}</h3>
            <p className="text-xs sm:text-sm text-[#64748B]">{t.tracking.support247Desc}</p>
          </div>
        </div>

        {/* Contact Section with background image */}
        <div className="mt-8 sm:mt-10 md:mt-12 text-center relative rounded-xl overflow-hidden shadow-lg">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1920&q=80"
              alt="Container ship at port"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0B1F3A]/90"></div>
          </div>
          <div className="relative z-10 p-6 sm:p-8 md:p-10">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3">{t.tracking.needHelp}</h3>
            <p className="text-sm sm:text-base text-gray-300 mb-5 sm:mb-6">{t.tracking.needHelpDesc}</p>
            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4 justify-center items-center">
              <a href="mailto:support@dutchcargoexpress.com" className="flex items-center space-x-2 text-gray-300 hover:text-[#FF6B00] transition-colors break-all">
                <EnvelopeIcon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm sm:text-base">support@dutchcargoexpress.com</span>
              </a>
              <span className="hidden sm:inline text-gray-500">|</span>
              <a href="tel:+496774420901" className="flex items-center space-x-2 text-gray-300 hover:text-[#FF6B00] transition-colors">
                <PhoneIcon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm sm:text-base">Berlin: +49 677 442 0901</span>
              </a>
              <span className="hidden sm:inline text-gray-500">|</span>
              <a href="tel:+12134774806" className="flex items-center space-x-2 text-gray-300 hover:text-[#FF6B00] transition-colors">
                <PhoneIcon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm sm:text-base">USA: +1 213-477-4806</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingPage;
